/**
 * The claim: what actually happened, and what it actually costs.
 *
 * Prefilled from the confirmed proposal — date, activity and the whole namelist **ticked**,
 * because unticking who did not come is the normal case and re-picking twenty people is not.
 *
 * Adding somebody who was not on the confirmed namelist is allowed and shows its consequence
 * immediately: they bring no entitlement (R-26), so their share falls into the excess, and HR
 * has to accept the exception. That is a warning, not a wall.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { EvidencePanel } from '@/components/EvidencePanel';
import { SettlementPanel } from '@/components/SettlementPanel';
import { ErrorNote, PageHeader } from '@/components/bits';
import {
  Alert,
  Badge,
  Button,
  Card,
  Dialog,
  Field,
  Input,
  Select,
  Table,
  Td,
  Textarea,
  Th,
} from '@/components/ui';
import { PAYMENT_MODES, activityLabel, partitionIssues } from '@/lib/policy';
import { formatCentsPlain, parseAmountToCents } from '@/lib/money';
import { formatDate, formatDateLong } from '@/lib/dates';

export function ClaimForm() {
  const params = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const cache = useQueryClient();

  const claimId = params.id ? Number(params.id) : null;
  const fromProposal = search.get('proposal') ? Number(search.get('proposal')) : null;

  /**
   * A claim has to exist on the server before anything can be edited: the attendee list, the
   * evidence link and the settlement are all server resources. So arriving with `?proposal=`
   * creates the draft and replaces the URL with it.
   *
   * One event has one claim, so arriving here for an event that already has one is the normal
   * case rather than an error — the API says which claim it is and this goes there.
   */
  const create = useMutation({
    mutationFn: (proposalId: number) => api.createClaim(proposalId),
    onSuccess: (created) => navigate(`/claims/${created.id}/edit`, { replace: true }),
    onError: (error) => {
      if (error instanceof ApiError && error.code === 'claim_exists') {
        const existing = (error.details as { claim_id?: number } | undefined)?.claim_id;
        if (existing) navigate(`/claims/${existing}`, { replace: true });
      }
    },
  });

  /**
   * A ref, not the mutation's own `isIdle`.
   *
   * React's StrictMode runs effects twice in development, and both passes read `isIdle` before
   * either mutation had flipped it — which POSTed twice. In production the double-mount does not
   * happen, but "the guard only works because the effect runs once" is not a guard.
   */
  const started = useRef(false);
  useEffect(() => {
    if (claimId !== null || fromProposal === null || started.current) return;
    started.current = true;
    create.mutate(fromProposal);
  }, [claimId, fromProposal, create]);

  if (claimId === null) {
    return create.error ? (
      <ErrorNote error={create.error} />
    ) : (
      <p className="text-muted-foreground text-sm">Starting the claim…</p>
    );
  }

  return <ClaimEditor claimId={claimId} cache={cache} />;
}

function ClaimEditor({
  claimId,
  cache,
}: {
  claimId: number;
  cache: ReturnType<typeof useQueryClient>;
}) {
  const navigate = useNavigate();
  const claim = useQuery({ queryKey: ['claim', claimId], queryFn: () => api.claim(claimId) });
  const settlement = useQuery({
    queryKey: ['settlement', claimId],
    queryFn: () => api.settlement(claimId),
  });

  const [cost, setCost] = useState('');
  const [mode, setMode] = useState('');
  const [payee, setPayee] = useState('');
  const [cardholder, setCardholder] = useState('');
  const [description, setDescription] = useState('');
  const [actualDate, setActualDate] = useState('');
  const [attendees, setAttendees] = useState<number[] | null>(null);
  const [askJustification, setAskJustification] = useState(false);
  const [justifications, setJustifications] = useState<Record<string, string>>({});
  const [loaded, setLoaded] = useState(false);

  const record = claim.data;

  useEffect(() => {
    if (loaded || !record) return;
    setCost(record.total_cost_cents > 0 ? formatCentsPlain(record.total_cost_cents) : '');
    setMode(record.payment.mode ?? '');
    setPayee(record.payment.payee_employee_id ? String(record.payment.payee_employee_id) : '');
    setCardholder(record.payment.cardholder_name ?? '');
    setDescription(record.payment.other_description ?? '');
    setActualDate(record.actual_date);
    setAttendees(record.attendees.map((attendee) => attendee.employee_id));
    setLoaded(true);
  }, [record, loaded]);

  /** The confirmed namelist: who was pre-vetted, so unticking is possible and adding is visible. */
  const namelist = useQuery({
    queryKey: ['proposal', record?.proposal.id],
    queryFn: () => api.proposal(record?.proposal.id as number),
    enabled: record !== undefined,
  });

  const roster = useQuery({
    queryKey: ['roster', record?.department.id],
    queryFn: () => api.employees({ department_id: record?.department.id as number, limit: 500 }),
    enabled: record !== undefined,
  });

  const costCents = cost.trim() === '' ? null : parseAmountToCents(cost);
  const costInvalid = cost.trim() !== '' && costCents === null;

  const candidates = useMemo(() => {
    const seen = new Map<
      number,
      { id: number; name: string; employeeNo: string; preVetted: boolean }
    >();
    for (const attendee of namelist.data?.attendees ?? []) {
      seen.set(attendee.employee_id, {
        id: attendee.employee_id,
        name: attendee.full_name,
        employeeNo: attendee.employee_no,
        preVetted: true,
      });
    }
    for (const employee of roster.data?.employees ?? []) {
      if (!seen.has(employee.id)) {
        seen.set(employee.id, {
          id: employee.id,
          name: employee.full_name,
          employeeNo: employee.employee_no,
          preVetted: false,
        });
      }
    }
    /*
     * The namelist first, then everyone else.
     *
     * Sorted by name alone, the six people who were actually vetted for this event ended up
     * below ten who were not — so the list a manager has to tick through led with the rows that
     * are almost always wrong. Pre-vetted is the answer nine times in ten; it goes first.
     */
    return [...seen.values()].sort((a, b) => {
      if (a.preVetted !== b.preVetted) return a.preVetted ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  }, [namelist.data, roster.data]);

  const editable = record?.status === 'draft' || record?.status === 'returned';
  const chosen = useMemo(() => new Set(attendees ?? []), [attendees]);

  const save = useMutation({
    mutationFn: async () => {
      if (!record) throw new Error('no claim');
      const payment =
        mode === ''
          ? {}
          : {
              payment_mode: mode,
              ...(mode === 'personal' ? { payee_employee_id: Number(payee) } : {}),
              ...(mode === 'corporate_card' ? { cardholder_name: cardholder.trim() } : {}),
              ...(mode === 'other' ? { other_mode_description: description.trim() } : {}),
            };
      await api.updateClaim(record.id, {
        actual_date: actualDate,
        ...(costCents === null ? {} : { total_cost_cents: costCents }),
        ...payment,
      });
      if ((attendees ?? []).length > 0)
        await api.setClaimAttendees(record.id, attendees as number[]);
    },
    onSuccess: async () => {
      await cache.invalidateQueries({ queryKey: ['claim', claimId] });
      await cache.invalidateQueries({ queryKey: ['settlement', claimId] });
    },
  });

  const submit = useMutation({
    mutationFn: async () => {
      await save.mutateAsync();
      return api.submitClaim(claimId, {
        ...(Object.keys(justifications).length > 0 ? { justifications } : {}),
      });
    },
    onSuccess: async () => {
      setAskJustification(false);
      // The record itself, not just the list. The editor has already put this claim in the
      // cache as a draft, and `staleTime` means the detail screen would render that stale copy
      // for the next fifteen seconds — showing "Draft" on a claim that was just submitted.
      await cache.invalidateQueries({ queryKey: ['claim', claimId] });
      await cache.invalidateQueries({ queryKey: ['settlement', claimId] });
      await cache.invalidateQueries({ queryKey: ['claims'] });
      navigate(`/claims/${claimId}`);
    },
    onError: (error) => {
      if (error instanceof ApiError && error.code === 'justification_required') {
        setAskJustification(true);
      }
    },
  });

  if (claim.isPending) return <p className="text-muted-foreground text-sm">Loading the claim…</p>;
  if (claim.error) return <ErrorNote error={claim.error} />;
  if (!record) return null;

  const issues = partitionIssues(record.issues);
  const modeNeeds = PAYMENT_MODES.find((entry) => entry.value === mode)?.needs ?? null;
  const paymentComplete =
    mode === '' ||
    (modeNeeds === 'payee' && payee !== '') ||
    (modeNeeds === 'cardholder' && cardholder.trim() !== '') ||
    (modeNeeds === 'description' && description.trim() !== '');

  const gateCrashers = (attendees ?? []).filter(
    (id) => candidates.find((candidate) => candidate.id === id)?.preVetted === false,
  );

  return (
    <>
      <PageHeader
        title={record.ref_no ? `Edit ${record.ref_no}` : 'Claim for a completed event'}
        subtitle={
          <>
            Against {record.proposal.ref_no ?? `proposal #${record.proposal.id}`} ·{' '}
            {activityLabel(record.activity_type)} · {record.department.name}
          </>
        }
      />

      {!editable ? (
        <Alert tone="warn" title="This claim can no longer be edited" className="mb-6">
          It has moved past the draft stage. Ask HR or your Sponsor to return it if something needs
          changing.
        </Alert>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-start">
        <div className="grid min-w-0 gap-6">
          <Card title="What happened">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Actual date"
                required
                hint={
                  actualDate
                    ? `${formatDateLong(actualDate)} · flagged if it crosses a budget year (R-24)`
                    : 'The date the event actually happened.'
                }
              >
                {(props) => (
                  <Input
                    {...props}
                    type="date"
                    value={actualDate}
                    disabled={!editable}
                    onChange={(event) => setActualDate(event.target.value)}
                  />
                )}
              </Field>

              <Field
                label="Total cost"
                required
                error={costInvalid ? 'Enter an amount like 1180 or 1180.10.' : null}
                hint={costInvalid ? null : 'GST-inclusive, as invoiced (R-01/R-02).'}
              >
                {(props) => (
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground text-sm">S$</span>
                    <Input
                      {...props}
                      inputMode="decimal"
                      value={cost}
                      disabled={!editable}
                      onChange={(event) => setCost(event.target.value)}
                    />
                  </div>
                )}
              </Field>

              <Field label="How was it paid for" required>
                {(props) => (
                  <Select
                    {...props}
                    value={mode}
                    disabled={!editable}
                    onChange={(event) => setMode(event.target.value)}
                  >
                    <option value="">Choose…</option>
                    {PAYMENT_MODES.map((entry) => (
                      <option key={entry.value} value={entry.value}>
                        {entry.label}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>

              {modeNeeds === 'payee' ? (
                <Field label="Reimburse" required hint="Whoever paid out of pocket.">
                  {(props) => (
                    <Select
                      {...props}
                      value={payee}
                      disabled={!editable}
                      onChange={(event) => setPayee(event.target.value)}
                    >
                      <option value="">Choose…</option>
                      {candidates.map((candidate) => (
                        <option key={candidate.id} value={candidate.id}>
                          {candidate.name} ({candidate.employeeNo})
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
              ) : null}

              {modeNeeds === 'cardholder' ? (
                <Field label="Cardholder" required>
                  {(props) => (
                    <Input
                      {...props}
                      value={cardholder}
                      disabled={!editable}
                      onChange={(event) => setCardholder(event.target.value)}
                    />
                  )}
                </Field>
              ) : null}

              {modeNeeds === 'description' ? (
                <div className="sm:col-span-2">
                  <Field label="How, then" required>
                    {(props) => (
                      <Input
                        {...props}
                        value={description}
                        disabled={!editable}
                        onChange={(event) => setDescription(event.target.value)}
                      />
                    )}
                  </Field>
                </div>
              ) : null}
            </div>
          </Card>

          <Card
            title={`Who actually came — ${(attendees ?? []).length} of ${(namelist.data?.attendees ?? []).length} on the namelist`}
            action={
              editable ? (
                <Button
                  size="sm"
                  onClick={() =>
                    setAttendees((namelist.data?.attendees ?? []).map((each) => each.employee_id))
                  }
                >
                  Reset to the namelist
                </Button>
              ) : null
            }
          >
            {gateCrashers.length > 0 ? (
              <Alert
                tone="warn"
                title={`${gateCrashers.length} were not on the namelist`}
                className="mb-4"
              >
                They contribute S$0, so their share becomes non-reimbursable excess (R-29) and HR
                must accept the exception. They are still welcome — this is about whose entitlement
                pays.
              </Alert>
            ) : null}

            <Table>
              <thead>
                <tr>
                  <Th className="w-8">
                    <span className="sr-only">Came</span>
                  </Th>
                  <Th>Name</Th>
                  <Th>No.</Th>
                  <Th>On the namelist</Th>
                </tr>
              </thead>
              <tbody>
                {candidates.map((candidate) => (
                  <tr key={candidate.id}>
                    <Td>
                      <input
                        type="checkbox"
                        className="accent-primary size-4"
                        disabled={!editable}
                        checked={chosen.has(candidate.id)}
                        aria-label={`${candidate.name} attended`}
                        onChange={(event) =>
                          setAttendees(
                            event.target.checked
                              ? [...(attendees ?? []), candidate.id]
                              : (attendees ?? []).filter((id) => id !== candidate.id),
                          )
                        }
                      />
                    </Td>
                    <Td>{candidate.name}</Td>
                    <Td className="tabular text-xs">{candidate.employeeNo}</Td>
                    <Td>
                      {candidate.preVetted ? (
                        <Badge tone="good">pre-vetted</Badge>
                      ) : chosen.has(candidate.id) ? (
                        <Badge tone="warn">not pre-vetted (R-26)</Badge>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>

          <EvidencePanel claim={record} editable={editable} />
        </div>

        <div className="grid gap-4 lg:sticky lg:top-28">
          <SettlementPanel
            settlement={settlement.data}
            pending={settlement.isFetching}
            error={settlement.error}
            attendeeCount={(attendees ?? []).length}
            contributingCount={
              settlement.data
                ? (attendees ?? []).length - settlement.data.gate_crashers.length
                : undefined
            }
          />

          {issues.blocking.length > 0 ? (
            <Alert tone="bad" title="Not ready to submit">
              <ul className="grid gap-1">
                {issues.blocking.map((issue) => (
                  <li key={issue.code}>
                    {issue.rule_id ? <strong>{issue.rule_id} — </strong> : null}
                    {issue.message}
                  </li>
                ))}
              </ul>
            </Alert>
          ) : null}

          {save.error ? <ErrorNote error={save.error} /> : null}
          {submit.error && !askJustification ? <ErrorNote error={submit.error} /> : null}

          {editable ? (
            <div className="grid gap-2">
              <Button
                variant="primary"
                busy={submit.isPending}
                disabled={
                  costInvalid ||
                  costCents === null ||
                  mode === '' ||
                  !paymentComplete ||
                  (attendees ?? []).length === 0 ||
                  issues.blocking.length > 0
                }
                onClick={() => {
                  if (issues.needJustification.length > 0) {
                    setAskJustification(true);
                    return;
                  }
                  submit.mutate();
                }}
              >
                Submit the claim
              </Button>
              <Button busy={save.isPending} onClick={() => save.mutate()}>
                Save
              </Button>
              <p className="text-muted-foreground text-xs">
                Event held {formatDate(record.actual_date)}. The claim window is 14 days from the
                event, or the year&rsquo;s grace deadline, whichever is earlier (R-24).
              </p>
            </div>
          ) : null}
        </div>
      </div>

      <Dialog
        open={askJustification}
        onClose={() => setAskJustification(false)}
        title="HR will need a reason"
        footer={
          <>
            <Button onClick={() => setAskJustification(false)}>Back</Button>
            <Button
              variant="primary"
              busy={submit.isPending}
              disabled={issues.needJustification.some(
                (issue) => (justifications[issue.rule] ?? '').trim() === '',
              )}
              onClick={() => submit.mutate()}
            >
              Submit with these reasons
            </Button>
          </>
        }
      >
        <p className="text-muted-foreground">
          Allowed — it just needs an explanation on the record. HR decides the exception before the
          claim can be approved.
        </p>
        {issues.needJustification.map((issue) => (
          <Field key={issue.rule} label={`${issue.rule} — why`} required>
            {(props) => (
              <>
                <p className="text-muted-foreground mb-1.5 text-xs">{issue.message}</p>
                <Textarea
                  {...props}
                  value={justifications[issue.rule] ?? ''}
                  onChange={(event) =>
                    setJustifications({ ...justifications, [issue.rule]: event.target.value })
                  }
                />
              </>
            )}
          </Field>
        ))}
        {submit.error ? <ErrorNote error={submit.error} /> : null}
      </Dialog>
    </>
  );
}
